import type { HoldPayload, HoldReasonKey, HoldReminder, HoldReminderKey } from '../interface/hold';

export interface HoldFormValues {
  readonly reason?: HoldReasonKey; // Path A: single-select; undefined until the coder picks one
  readonly note: string; // always OPTIONAL (all 3 reasons are concrete backend holds; no "describe")
  readonly reminder?: HoldReminderKey;
  readonly customReminderAt: string; // raw <input type="datetime-local"> value ('' when unset)
}

export type HoldValidationError =
  | 'reason-required'
  | 'custom-reminder-required'
  | 'custom-reminder-invalid'
  | 'custom-reminder-past';

type Result =
  | { ok: true; payload: HoldPayload }
  | { ok: false; errors: HoldValidationError[] };

/** Pure. `now` is INJECTED (never `new Date()` inside) so validation is deterministic in tests. */
export function buildHoldPayload(values: HoldFormValues, now: Date): Result {
  const errors: HoldValidationError[] = [];
  const reason = values.reason;
  const note = values.note.trim();

  // The only hard requirement is a reason. The note is optional for every reason (the old
  // "Other -> describe + min-length note" coupling was removed when "Other" became the plain "Hold"
  // reason). The reminder branches stay for the parity follow-up (PT-3); the UI keeps them disabled.
  if (!reason) errors.push('reason-required');

  let reminder: HoldReminder | undefined;
  if (values.reminder && values.reminder !== 'custom') {
    reminder = { kind: 'preset', key: values.reminder };
  } else if (values.reminder === 'custom') {
    const raw = values.customReminderAt.trim();
    if (raw.length === 0) {
      errors.push('custom-reminder-required');
    } else {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) errors.push('custom-reminder-invalid');
      else if (d.getTime() <= now.getTime()) errors.push('custom-reminder-past');
      else reminder = { kind: 'custom', at: d.toISOString() };
    }
  }

  if (errors.length > 0 || !reason) return { ok: false, errors };

  const payload: HoldPayload = {
    reason, // single-select (Path A); narrowed non-undefined by the guard above
    ...(note.length > 0 ? { note } : {}),
    ...(reminder ? { reminder } : {})
  };
  return { ok: true, payload };
}
